import { useEffect, useMemo } from 'react'
import { useParams, withRouter } from 'react-router-dom'
import { ContentBody, ContentHeader } from 'components/shared/content'
import * as H from 'history'
import { useDeletePcrRunApi, useFetchPcrRunApi, usePutPcrRunApi } from 'api/pcr_run'
import { PcrCell, PcrRunViewModel } from 'view_models/pcr_run_view_model'
import { Button, Modal, OverlayTrigger, Table, Tooltip, Form as BSForm, Alert } from 'react-bootstrap'
import { Probe } from 'entities/probe'
import { Dna } from 'entities/dna'
import { PcrProtocol } from 'entities/pcr_protocol'
import { useState } from 'react'
import { ALPHABET, setPageSize, swipeBack, convertPos2Str, getFontColorByBackground } from 'utils/util'
import { Flex } from 'components/shared/flex'
import { CheckboxField, FileField, ReagentItemSelectField, InputField, SelectField } from 'components/shared/input'
import { PcrRun, PcrRunForm, PcrRunProbes, PcrRunProbesForm } from 'entities/pcr_run'
import { useEffectSkipFirst, useForm } from 'utils/hooks'
import { useContext } from 'react'
import { GlobalStateContext } from 'contexts/global_state_context'
import './print.css'
import { Landscape } from 'components/shared/print'
import { useFetchPcrProtocolsApi } from 'api/pcr_protocol'
import { PcrProtocolViewModel } from 'view_models/pcr_protocol_view_model'
import PcrErrorModal from './pcr_error_modal'
import { usePutPcrRunProbesApi } from 'api/pcr_run_probes'
import { ProcessLogsModal } from './process_log_modal'
import { ThemeContext } from 'contexts/theme_context'
import { useFetchEquipmentsApi } from 'api/equipment'
import { EquipmentSearchForm } from 'entities/equipment'
import { PcrErrorSearchForm, WellInfo } from 'entities/pcr_error'
import { useFetchPcrErrorsApi } from 'api/pcr_error'
import { ExpProtocolV1 } from './exp_protocol_v1'
import { DispenseAmount, ExpProtocolV2 } from './exp_protocol_v2'

type Props = {
    history: H.History
}

const PcrRun384Page = (props: Props) => {
    const globalState = useContext(GlobalStateContext)
    const theme = useContext(ThemeContext)
    const params = useParams<{ id: string }>()
    const runApi = useFetchPcrRunApi()
    const [showPlot, setShowPlot] = useState<boolean>(false)
    const [colorType, setColorType] = useState<'dna' | 'probe'>('probe')
    const [hoveredPcrCell, setHoveredPcrCell] = useState<PcrCell>({})
    const [showUpload, setShowUpload] = useState<boolean>(false)
    const updateRunApi = usePutPcrRunApi()
    const uploadForm = useForm<PcrRunForm>({})
    const protocolsApi = useFetchPcrProtocolsApi()
    const [count, setCount] = useState<number>(0)
    const updateStockForm = useForm<PcrRunProbesForm[]>([])
    const updateStockApi = usePutPcrRunProbesApi()
    const settingsForm = useForm<{ dnaCount?: number; probeCount?: number; dnaDispenseCount?: number; probeDispenseCount?: number }>({})
    const equipmentSearchForm = useForm<EquipmentSearchForm>({ type: 'PCR' })
    const equipmentsApi = useFetchEquipmentsApi(equipmentSearchForm)
    const [selectedWells, setSelectedWells] = useState<WellInfo[]>([])
    const pcrErrorsSearchForm = useForm<PcrErrorSearchForm>({ pcrRunId: Number(params.id) })
    const errorsApi = useFetchPcrErrorsApi(pcrErrorsSearchForm)
    const dispenseForm = useForm<DispenseAmount>({})
    const settings = useForm<{ isOmit: boolean }>({ isOmit: true })
    const deleteApi = useDeletePcrRunApi()

    useEffect(() => {
        globalState.setLoading(false)
        runApi.execute(Number(params.id))
        protocolsApi.execute()
        errorsApi.execute()
        setPageSize('landscape')
        equipmentsApi.execute()
        swipeBack.disable()
        return () => {
            globalState.setLoading(false)
            swipeBack.enable()
        }
    }, [])

    useEffectSkipFirst(() => {
        globalState.setLoading(runApi.loading)
        if (runApi.isSuccess()) {
            const { probes, dnaSet, productItems, pcrRunProbes, pcrProtocol, ...rest } = runApi.response.pcrRun!
            uploadForm.set({ ...rest, status: undefined, rawData: undefined })
            updateStockForm.set(JSON.parse(JSON.stringify(runApi.response.pcrRun!.pcrRunProbes!)))

            const protocol = runApi.response.pcrRun?.pcrProtocol
            if (!protocol) {
                return
            }
            let nDivision = 2
            if (protocol.sampleCount) {
                if (protocol.sampleCount >= 64) {
                    nDivision = 8
                } else if (protocol.sampleCount >= 40) {
                    nDivision = 5
                } else if (protocol.sampleCount === 32) {
                    nDivision = 4
                } else if (protocol.sampleCount === 24) {
                    nDivision = 3
                }
            }
            const sampleDispense = Math.ceil((protocol.sampleCount || 0) / nDivision)
            const probeDispense = Math.ceil((protocol.probeCount || 0) / 8)

            settingsForm.set({
                dnaCount: runApi.response.pcrRun?.pcrProtocol?.sampleCount,
                probeCount: runApi.response.pcrRun?.pcrProtocol?.probeCount,
                dnaDispenseCount: sampleDispense,
                probeDispenseCount: probeDispense,
            })
        }
    }, [runApi.loading])

    useEffectSkipFirst(() => {
        globalState.setLoading(updateStockApi.loading)
        if (updateStockApi.isSuccess()) {
            runApi.execute(Number(params.id))
        }
    }, [updateStockApi.loading])

    useEffectSkipFirst(() => {
        globalState.setLoading(updateRunApi.loading)
        if (updateRunApi.isSuccess()) {
            runApi.execute(Number(params.id))
        } else if (updateRunApi.isFailure()) {
            runApi.execute(Number(params.id))
        }
    }, [updateRunApi.loading])

    useEffectSkipFirst(() => {
        if (deleteApi.isSuccess()) {
            props.history.push('/pcr_runs')
        }
    }, [deleteApi.loading])

    const pcrRun = () => {
        return runApi.response.pcrRun!
    }

    const protocol = () => {
        return pcrRun().pcrProtocol!
    }

    const getVM = () => {
        return new PcrRunViewModel(pcrRun())
    }

    const omitDnaCode = (dna: Dna): string => {
        if (!settings.object.isOmit) {
            return dna.code!
        }
        const list = dna.code!.split('_').slice(0, 2)
        return list.join('_')
    }

    const omitProbeName = (probe: Probe): string => {
        if (!settings.object.isOmit) {
            return probe.name!
        }
        if (probe.name! === 'ath-miR159a') {
            return '159a'
        }
        let shortName = probe.name!
        const removeWords = ['hsa-miR-', 'hsa-miR', 'cel-miR-', 'cel-lin-', 'ath-miR-', 'ath-miR']
        removeWords.forEach((removeWord) => {
            shortName = shortName.replace(removeWord, '')
        })
        return shortName
    }

    const filteredProtocols: PcrProtocol[] = useMemo(() => {
        if (!runApi.response.pcrRun || protocolsApi.response.results.length === 0) {
            return []
        }
        const pcrRun: PcrRun = runApi.response.pcrRun
        return protocolsApi.response.results
            .filter((protocol) => {
                // blankと標準尿分 dnaには2を足す
                const blankCount = 2
                return pcrRun.pcrRunProbes!.length! <= protocol.probeCount! && pcrRun.dnaSet!.length + blankCount <= protocol.sampleCount!
            })
            .sort((a, b) => {
                if (a.sampleCount! === b.sampleCount!) {
                    return a.probeCount! - b.probeCount!
                }
                return a.sampleCount! - b.sampleCount!
            })
    }, [protocolsApi.response.results, runApi.response.pcrRun])

    if (!runApi.response.pcrRun) {
        return <></>
    }

    const wellArray = getVM().createWellArray()
    return (
        <>
            <Modal show={showUpload} onHide={() => setShowUpload(false)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Rawデータアップロード</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert>1. Wellのエラーがある場合はアップロード前に必ずエラー箇所を登録してください</Alert>
                    {/* <div style={{ padding: '0 20px' }}>
                        <PcrErrors pcrRunId={pcrRun().id!} array384={getVM().create384Array(wellArray)} />
                    </div> */}
                    <Alert style={{ marginTop: 30 }}>2. MasterMixを入力</Alert>
                    <div style={{ padding: '0 20px' }}>
                        <ReagentItemSelectField form={uploadForm} label="Master mix" attr="masterMixReagentItemId" reagentKind="master_mix" />

                        <ReagentItemSelectField form={uploadForm} label="Free water" attr="pcrFreeWaterReagentItemId" reagentKind="free_water" />
                    </div>
                    <Alert style={{ marginTop: 30 }}>3. データをアップロードしてください</Alert>
                    <div style={{ padding: '0 20px' }}>
                        <FileField form={uploadForm} attr="rawData" label="Rawデータ" />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowUpload(false)}>
                        キャンセル
                    </Button>
                    <Button
                        variant="success"
                        // disabled={!uploadForm.object.rawData}
                        onClick={() => {
                            updateRunApi.execute(uploadForm)
                            setShowUpload(false)
                        }}
                    >
                        アップロード
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={showPlot}
                fullscreen
                onHide={() => {
                    setShowPlot(false)
                    setCount(count + 1)
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title>384プロット</Modal.Title>
                    <Button
                        onClick={() => {
                            setColorType(colorType === 'probe' ? 'dna' : 'probe')
                        }}
                        style={{ marginLeft: 10 }}
                    >
                        色タイプを{colorType === 'probe' ? 'cDNA' : 'Probe'}に切り替え
                    </Button>

                    {selectedWells.length > 0 && (
                        <Flex alignItems="center" style={{ marginLeft: 20 }}>
                            <div>{selectedWells.length} 件のWellを選択中</div>
                            <PcrErrorModal
                                pcrRunId={pcrRun().id!}
                                selectedWells={selectedWells}
                                onSuccess={() => {
                                    setSelectedWells([])
                                    errorsApi.execute()
                                }}
                            />
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setSelectedWells([])
                                }}
                            >
                                選択解除
                            </Button>
                        </Flex>
                    )}
                </Modal.Header>
                <Modal.Body>
                    <Flex>
                        <Table className="info-384" bordered striped>
                            <thead>
                                {(() => {
                                    if (colorType === 'dna') {
                                        return (
                                            <tr>
                                                <th>No</th>
                                                <th className="info-384">cDNA ID</th>
                                            </tr>
                                        )
                                    } else {
                                        return (
                                            <tr>
                                                <th>No</th>
                                                <th className="info-384">Probe</th>
                                            </tr>
                                        )
                                    }
                                })()}
                            </thead>
                            <tbody>
                                {(() => {
                                    if (colorType === 'dna') {
                                        const rows = getVM()
                                            .sortedDnaSet()
                                            .map((dna, index) => {
                                                return (
                                                    <tr key={'reference-dna-' + dna.id}>
                                                        <td style={{ width: 20, textAlign: 'center' }}>{index + 1}</td>
                                                        <td
                                                            className={
                                                                typeof hoveredPcrCell.dna !== 'string' && hoveredPcrCell.dna?.id === dna.id ? 'flashing' : ''
                                                            }
                                                            style={{ backgroundColor: getVM().getDnaColor(dna) }}
                                                        >
                                                            <Flex>
                                                                <div style={{ padding: '2px 5px', borderRadius: 5, backgroundColor: 'white' }}>
                                                                    {getVM().getDnaNumber(dna)}
                                                                </div>
                                                                <div>{dna.code}</div>
                                                            </Flex>
                                                        </td>
                                                    </tr>
                                                )
                                            })
                                        rows?.push(
                                            <tr>
                                                <td style={{ width: 20, textAlign: 'center' }}>{pcrRun().dnaSet?.length ? pcrRun().dnaSet!.length + 1 : ''}</td>
                                                <td style={{ color: 'white', backgroundColor: 'black' }}>qPCR_standard</td>
                                            </tr>
                                        )
                                        return rows
                                    } else {
                                        return pcrRun()
                                            .pcrRunProbes?.sort((a, b) => {
                                                if ((a.probe!.isQc && b.probe!.isQc) || (!a.probe!.isQc && !b.probe!.isQc)) {
                                                    if (a.probe!.name! < b.probe!.name!) {
                                                        return 1
                                                    } else if (a.probe!.name! > b.probe!.name!) {
                                                        return -1
                                                    } else {
                                                        return 0
                                                    }
                                                } else if (a.probe!.isQc) {
                                                    return -1
                                                } else {
                                                    return 1
                                                }
                                            })
                                            ?.map((prp, index) => {
                                                const probe = prp.probe!
                                                return (
                                                    <tr key={'refelence-probe-' + probe.id}>
                                                        <td style={{ width: 20, textAlign: 'center' }}>{index + 1}</td>
                                                        <td
                                                            className={hoveredPcrCell.probe?.id === probe.id ? 'flashing' : ''}
                                                            style={{ backgroundColor: getVM().getProbeColor(probe) }}
                                                        >
                                                            <Flex>
                                                                <div style={{ padding: '2px 5px', borderRadius: 5, backgroundColor: 'white' }}>
                                                                    {getVM().getProbeNumber(probe)}
                                                                </div>
                                                                <div>{probe.name}</div>
                                                            </Flex>
                                                        </td>
                                                    </tr>
                                                )
                                            })
                                    }
                                })()}
                            </tbody>
                        </Table>
                        <Table bordered striped style={{ textAlign: 'center' }}>
                            <thead>
                                <tr>
                                    <th></th>
                                    {new Array(24).fill(0).map((_, index) => {
                                        return <th key={'384-header-' + index}>{index + 1}</th>
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {getVM()
                                    .create384Array(wellArray)
                                    .map((rowData, row) => {
                                        return (
                                            <tr key={'plot-array-row-' + row}>
                                                <td>{ALPHABET[row]}</td>
                                                {rowData.map((colData, col) => {
                                                    if (!colData.dna && !colData.probe) {
                                                        return <td key={'plot_array-col-' + col}></td>
                                                    }
                                                    let color: string | undefined = 'black'
                                                    if (colData.dna === undefined || colData.probe === undefined) {
                                                        color = '#fff0'
                                                    } else if (typeof colData.dna !== 'string' && colData.dna && colorType === 'dna') {
                                                        color = getVM().getDnaColor(colData.dna as Dna)
                                                    } else if (colData.probe && colorType === 'probe') {
                                                        color = getVM().getProbeColor(colData.probe)
                                                    }

                                                    const location = convertPos2Str(row + 1, col + 1)
                                                    const isSelected = selectedWells.some((wellInfo) => wellInfo.well === location)
                                                    const hasError = errorsApi.response.results?.some((error) => error.well === location)
                                                    const isGray = isSelected || hasError

                                                    if (isGray) {
                                                        color = '#888'
                                                    }
                                                    return (
                                                        <td
                                                            key={'plot_array-col-' + col}
                                                            onClick={() => {
                                                                if (hasError) {
                                                                    return
                                                                }
                                                                const location = convertPos2Str(row + 1, col + 1)
                                                                const l = selectedWells
                                                                const index = l.findIndex((wellInfo) => wellInfo.well === location)
                                                                if (index >= 0) {
                                                                    l.splice(index, 1)
                                                                } else {
                                                                    l.push({
                                                                        well: location,
                                                                        probeName: colData.probe?.name,
                                                                        dnaCode: typeof colData.dna === 'string' ? colData.dna : colData.dna!.code!,
                                                                    })
                                                                }
                                                                setSelectedWells(l)
                                                            }}
                                                            className={isSelected ? 'flashing' : ''}
                                                        >
                                                            <OverlayTrigger
                                                                onEnter={() => {
                                                                    setHoveredPcrCell(colData)
                                                                }}
                                                                onExit={() => {
                                                                    setHoveredPcrCell({})
                                                                }}
                                                                placement="bottom"
                                                                delay={{ show: 0, hide: 0 }}
                                                                overlay={
                                                                    <Tooltip id="probe-dna-tooltip">
                                                                        <table style={{ textAlign: 'left' }}>
                                                                            <tbody>
                                                                                {colData.dna && (
                                                                                    <tr>
                                                                                        <th style={{ width: 80 }}>cDNA ID</th>
                                                                                        <td>
                                                                                            {typeof colData.dna !== 'string' ? colData.dna?.code : colData.dna}
                                                                                        </td>
                                                                                    </tr>
                                                                                )}
                                                                                {colData.probe && (
                                                                                    <tr>
                                                                                        <th>Probe</th>
                                                                                        <td>{colData.probe?.name}</td>
                                                                                    </tr>
                                                                                )}
                                                                            </tbody>
                                                                        </table>
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                {
                                                                    <div
                                                                        style={{
                                                                            width: 30,
                                                                            height: 30,
                                                                            backgroundColor: color,
                                                                            borderRadius: 20,
                                                                            margin: 'auto',
                                                                        }}
                                                                    ></div>
                                                                }
                                                            </OverlayTrigger>
                                                        </td>
                                                    )
                                                })}
                                            </tr>
                                        )
                                    })}
                            </tbody>
                        </Table>
                    </Flex>
                </Modal.Body>
                <Modal.Footer></Modal.Footer>
            </Modal>
            <ContentHeader
                className="no-print"
                headerTitle={runApi.response.pcrRun.runId}
                left={
                    pcrRun().pcrProtocol?.id && (
                        <>
                            <Button onClick={() => setShowPlot(true)}>384プロット</Button>

                            {runApi.response.pcrRun.pcrProtocol?.id ? (
                                <>
                                    <Button variant="secondary" onClick={() => getVM().downloadRunSettingFile()}>
                                        設定ファイル出力
                                    </Button>
                                    {(!pcrRun().rawData || count >= 3) && (
                                        <Button variant="success" onClick={() => setShowUpload(true)}>
                                            Rawデータアップロード
                                        </Button>
                                    )}
                                    {!!pcrRun().pcrProcessLogs?.length && (
                                        <ProcessLogsModal
                                            pcrProcessLogs={pcrRun().pcrProcessLogs}
                                            onClickShowAll={() => props.history.push('/pcr_process_logs')}
                                        />
                                    )}
                                    <a
                                        href={'https://docs.google.com/spreadsheets/d/1mp_W4HWC1uMvcVgvmdR5stKdhuwDcqxjOx9uTpWp6mo/edit?pli=1#gid=785622127'}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        作業シート
                                    </a>
                                </>
                            ) : (
                                <>
                                    <Button
                                        variant="success"
                                        onClick={() => {
                                            updateRunApi.execute(uploadForm)
                                        }}
                                    >
                                        プロトコル確定
                                    </Button>
                                </>
                            )}
                        </>
                    )
                }
                right={
                    <>
                        <CheckboxField form={settings} attr="isOmit" checkboxLabel="省略表示" />

                        {runApi.response.pcrRun.status == 'NOT_STARTED' && (
                            <Button
                                variant="danger"
                                size="sm"
                                onClick={(e) => {
                                    globalState.showConfirm(
                                        <div>
                                            {runApi.response.pcrRun?.runId}を削除しますか？<br></br>
                                            本当に削除する場合のみ以下のフォームに「削除」と入力してOKを押してください。<br></br>※
                                            削除は必ず管理者が行なってください。
                                        </div>,

                                        () => {
                                            deleteApi.execute(runApi.response.pcrRun!.id!)
                                        },
                                        { confirmWord: '削除' }
                                    )
                                }}
                            >
                                転記取消
                            </Button>
                        )}
                    </>
                }
            />
            <ContentBody>
                {pcrRun().pcrRunProbes?.some((prp) => !prp.probeStock) && (
                    <Alert className="no-print" variant="warning">
                        ロットが設定されていないプローブがあります
                    </Alert>
                )}

                <div>
                    {protocol().version === 1 ? (
                        <div>
                            プロトコル: opentron_{pcrRun().pcrProtocol?.sampleCount}cDNA_{pcrRun().pcrProtocol?.probeCount}probe.json
                        </div>
                    ) : (
                        <div>
                            プロトコル: opentron_{pcrRun().pcrProtocol?.sampleCount}sample_{pcrRun().pcrProtocol?.probeCount}probe.py
                        </div>
                    )}
                </div>
                <div className="no-print">
                    <Flex>
                        <div>保守管理台帳: </div>
                        {equipmentsApi.response.results?.map((equipment) => {
                            return (
                                <a
                                    key={'equipment-' + equipment.id}
                                    href={`/ledgers/maintenance_ledgers?equipmentId=${equipment.id}&equipmentName=${equipment.name}&equipmentNumber=${equipment.number}`}
                                    target={'_blank'}
                                    rel="noreferrer"
                                >
                                    {equipment.name}
                                </a>
                            )
                        }) ?? '--'}
                    </Flex>
                </div>
                {!runApi.response.pcrRun.pcrProtocol?.id && (
                    <BSForm.Select
                        style={{ width: 400 }}
                        className="form-control"
                        required
                        onChange={(e) => {
                            const protocolId = parseInt(e.target.value)
                            uploadForm.update((f) => {
                                f.pcrProtocol = protocolsApi.response.results.find((p) => p.id === protocolId)
                                f.pcrProtocolId = protocolId
                            })
                        }}
                        value={pcrRun().pcrProtocol?.id}
                    >
                        {<option value={''}>プロトコルを選択してください</option>}
                        {filteredProtocols.map((protocol: PcrProtocol) => {
                            return (
                                <option key={'pcr_protocol_' + protocol.id} value={protocol.id}>
                                    {new PcrProtocolViewModel(protocol).name()}
                                </option>
                            )
                        })}
                    </BSForm.Select>
                )}
                {pcrRun().id && (
                    <Landscape className="print-area">
                        <Flex className="no-screen child-margin-c-15" style={{ fontSize: 18 }}>
                            <div style={{ marginRight: 10 }}>{pcrRun().runId}</div>
                            <div style={{ marginRight: 10 }}>{pcrRun().user?.fullName}</div>
                            <div>{getVM().createdAtText()}</div>
                        </Flex>
                        <div className="well">
                            <Table bordered style={{ width: 'auto', backgroundColor: 'white' }}>
                                <thead>
                                    <tr>
                                        <th></th>
                                        {new Array(12).fill(0).map((_, index) => {
                                            return (
                                                <th className="well-head" key={'probe-head-' + index}>
                                                    {index + 1}
                                                </th>
                                            )
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {wellArray.map((rowData, row) => {
                                        return (
                                            <tr key={'probe-body-row-' + row}>
                                                <td>{ALPHABET[row]}</td>
                                                {rowData.map((colData, col) => {
                                                    if (!colData) {
                                                        return <td key={'probe-body-row-' + row + 'col-' + col}>--</td>
                                                    } else if ((colData as Probe).name) {
                                                        const probe = colData as Probe
                                                        const color = getVM().getProbeColor(probe)
                                                        const isQCCol = wellArray[row][col + 1] === 'qPCR_standard'
                                                        return (
                                                            <td style={{ backgroundColor: color }} key={'probe-body-row-' + row + 'col-' + col}>
                                                                <Flex
                                                                    justifyContent="space-between"
                                                                    alignItems="center"
                                                                    style={{ fontSize: settings.object.isOmit ? 18 : 'inherit' }}
                                                                >
                                                                    <Flex alignItems="center">
                                                                        <div style={{ padding: '2px 5px', borderRadius: 5, backgroundColor: 'white' }}>
                                                                            {getVM().getProbeNumber(probe)}
                                                                        </div>
                                                                        <div style={{ color: getFontColorByBackground(color!), position: 'relative' }}>
                                                                            {probe.isQc && (
                                                                                <div style={{ fontSize: 10, position: 'absolute', top: -9, left: 0 }}>QC</div>
                                                                            )}
                                                                            {omitProbeName(probe)}
                                                                        </div>
                                                                    </Flex>
                                                                    <div style={{ padding: '2px 5px', borderRadius: 5, backgroundColor: 'white' }}>
                                                                        {dispenseForm.object.probeMix && isQCCol
                                                                            ? `${dispenseForm.object.standardProbeMix!}μl`
                                                                            : dispenseForm.object.probeMix
                                                                            ? `${dispenseForm.object.probeMix}μl`
                                                                            : ''}
                                                                    </div>
                                                                </Flex>
                                                            </td>
                                                        )
                                                    } else if ((colData as Dna).code) {
                                                        const dna = colData as Dna
                                                        const color = getVM().getDnaColor(dna)
                                                        const thresCount = pcrRun().probes!.length % 8
                                                        const isLessAmount = thresCount !== 0 && thresCount < row + 1
                                                        const probeColCount = Math.ceil(protocol().probeCount! / 8)
                                                        const amount = probeColCount * 3 * 5.4 + 10.8
                                                        return (
                                                            <td style={{ backgroundColor: color }} key={'probe-body-row-' + row + 'col-' + col}>
                                                                {dna.id === pcrRun().standardDnaId ? (
                                                                    <span className="standard-urine">{dna.code}(標準尿)</span>
                                                                ) : (
                                                                    <Flex
                                                                        justifyContent="space-between"
                                                                        alignItems="center"
                                                                        style={{ fontSize: settings.object.isOmit ? 18 : 'inherit' }}
                                                                    >
                                                                        <Flex alignItems="center">
                                                                            <div style={{ padding: '2px 5px', borderRadius: 5, backgroundColor: 'white' }}>
                                                                                {getVM().getDnaNumber(dna)}
                                                                            </div>
                                                                            <div style={{ color: getFontColorByBackground(color!) }}>{omitDnaCode(dna)}</div>
                                                                        </Flex>
                                                                        <div style={{ padding: '2px 5px', borderRadius: 5, backgroundColor: 'white' }}>
                                                                            {dispenseForm.object.dnaMix
                                                                                ? `${Math.round((amount - (isLessAmount ? 16.2 : 0)) * 10) / 10}μl`
                                                                                : ''}
                                                                        </div>
                                                                    </Flex>
                                                                )}
                                                            </td>
                                                        )
                                                    } else {
                                                        return (
                                                            <td style={{ backgroundColor: '#444' }} key={'probe-body-row-' + row + 'col-' + col}>
                                                                <Flex
                                                                    justifyContent="space-between"
                                                                    style={{ fontSize: settings.object.isOmit ? 18 : 'inherit' }}
                                                                >
                                                                    <div style={{ color: 'white' }}>{colData as string}</div>
                                                                    <div style={{ padding: '2px 5px', borderRadius: 5, backgroundColor: 'white' }}>
                                                                        {dispenseForm.object.standardDnaMix ? `${dispenseForm.object.standardDnaMix}μl` : ''}
                                                                    </div>
                                                                </Flex>
                                                            </td>
                                                        )
                                                    }
                                                })}
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </Table>
                        </div>

                        <Flex className="protocol-area" style={{ width: 2300 }}>
                            {protocol().version === 1 ? (
                                <ExpProtocolV1 pcrRun={pcrRun()} vm={getVM()} />
                            ) : (
                                protocol() && <ExpProtocolV2 pcrRun={pcrRun()} form={dispenseForm} />
                            )}
                            <div style={{ marginTop: 0, width: 1200 }}>
                                <div className="no-print">
                                    <div style={{ backgroundColor: 'white', marginRight: 10, marginBottom: 20 }}>
                                        <div style={{ background: theme.brand, padding: '5px 15px', color: 'white' }}>Probe</div>

                                        <div style={{ padding: 10 }}>
                                            <Flex alignItems="center">
                                                <Alert style={{ flexGrow: 1 }} variant="danger">
                                                    赤枠のフォームは保存されていないので注意
                                                </Alert>
                                                <Button
                                                    variant="success"
                                                    onClick={() => {
                                                        updateStockApi.execute(updateStockForm)
                                                    }}
                                                >
                                                    ロットを保存
                                                </Button>
                                            </Flex>
                                            <Table style={{ marginRight: 10 }} bordered striped>
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: 80 }}>No</th>
                                                        <th style={{ width: 80 }}>Box</th>
                                                        <th style={{ width: 200 }}>Probe</th>
                                                        <th style={{ width: 430 }}>Lot</th>
                                                        <th style={{ minWidth: 200 }}>備考</th>
                                                        <th style={{ width: 170 }}></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(() => {
                                                        return getVM()
                                                            .sortedPcrProbes()
                                                            .map((prp: PcrRunProbes, index) => {
                                                                const probe = prp.probe!
                                                                return (
                                                                    <tr key={'refelence-probe-' + probe.id}>
                                                                        <td style={{ width: 20, textAlign: 'center', verticalAlign: 'middle' }}>{index + 1}</td>
                                                                        <td style={{ verticalAlign: 'middle' }}>{probe.box}</td>
                                                                        <td
                                                                            className={hoveredPcrCell.probe?.id === probe.id ? 'flashing' : ''}
                                                                            style={{ backgroundColor: getVM().getProbeColor(probe), verticalAlign: 'middle' }}
                                                                        >
                                                                            <Flex>
                                                                                <div style={{ padding: '2px 5px', borderRadius: 5, backgroundColor: 'white' }}>
                                                                                    {getVM().getProbeNumber(probe)}
                                                                                </div>
                                                                                <div style={{ color: getFontColorByBackground(getVM().getProbeColor(probe)!) }}>
                                                                                    {probe.name}
                                                                                </div>
                                                                            </Flex>
                                                                        </td>
                                                                        {(() => {
                                                                            const probeStocks = probe.probeStocks?.filter((ps) => !ps.usedDate)
                                                                            // if (!probe.probeStocks?.every((probeStock) => probeStock.usedCount === 0)) {
                                                                            //     probeStocks = probe.probeStocks?.filter((probeStock) => probeStock!.usedCount! > 0)
                                                                            // }

                                                                            const selectItems =
                                                                                probeStocks?.map((probeStock) => {
                                                                                    return {
                                                                                        label: `(${
                                                                                            probeStock.usedCount ? probeStock.usedCount + '回' : '未'
                                                                                        }) ${probeStock.lotNumber} / ${probeStock.vialId} / 開封日: ${
                                                                                            probeStock.openedDate || '-'
                                                                                        }`,
                                                                                        value: probeStock.id!,
                                                                                    }
                                                                                }) || []
                                                                            return (
                                                                                <>
                                                                                    <td>
                                                                                        {prp.probeStock && !!prp.probeStock.usedDate ? (
                                                                                            <div>{prp.probeStock?.lotNumber}</div>
                                                                                        ) : selectItems.length > 0 ? (
                                                                                            <Flex>
                                                                                                <SelectField
                                                                                                    isValid={
                                                                                                        !!prp.probeStock &&
                                                                                                        prp.probeStock.id ===
                                                                                                            updateStockForm.getValue([index, 'probeStockId'])
                                                                                                    }
                                                                                                    isInvalid={
                                                                                                        !!updateStockForm.getValue([index, 'probeStockId']) &&
                                                                                                        prp.probeStockId !==
                                                                                                            updateStockForm.getValue([index, 'probeStockId'])
                                                                                                    }
                                                                                                    disabled={prp.isUsedStock}
                                                                                                    form={updateStockForm}
                                                                                                    attr={[index, 'probeStockId']}
                                                                                                    selectItems={selectItems}
                                                                                                    style={{ marginBottom: 0 }}
                                                                                                    includeBlank
                                                                                                />
                                                                                            </Flex>
                                                                                        ) : (
                                                                                            <div></div>
                                                                                        )}
                                                                                    </td>
                                                                                    <td>
                                                                                        <InputField
                                                                                            form={updateStockForm}
                                                                                            attr={[index, 'note']}
                                                                                            style={{ marginBottom: 0 }}
                                                                                            isValid={
                                                                                                !!prp.note &&
                                                                                                prp.note === updateStockForm.getValue([index, 'note'])
                                                                                            }
                                                                                            isInvalid={
                                                                                                !!updateStockForm.getValue([index, 'note']) &&
                                                                                                prp.note !== updateStockForm.getValue([index, 'note'])
                                                                                            }
                                                                                        />
                                                                                    </td>
                                                                                    <td style={{ verticalAlign: 'middle' }}>
                                                                                        {(updateStockForm.getValue([index, 'probeStockId']) ||
                                                                                            prp.probeStockId) && (
                                                                                            <div style={{ fontSize: 12 }}>
                                                                                                <CheckboxField
                                                                                                    form={updateStockForm}
                                                                                                    attr={[index, 'isUsedStock']}
                                                                                                    checkboxLabel="今回で廃棄(使用済)"
                                                                                                />
                                                                                            </div>
                                                                                        )}
                                                                                    </td>
                                                                                </>
                                                                            )
                                                                        })()}
                                                                    </tr>
                                                                )
                                                            })
                                                    })()}
                                                </tbody>
                                            </Table>
                                            <Button
                                                variant="success"
                                                onClick={() => {
                                                    updateStockApi.execute(updateStockForm)
                                                }}
                                            >
                                                ロットを保存
                                            </Button>
                                        </div>
                                    </div>
                                </div>

                                <Flex>
                                    <div style={{ backgroundColor: 'white' }} className="no-screen run-required-list">
                                        <div style={{ background: theme.brand, padding: '5px 15px', color: 'white' }}>Probe</div>

                                        <div style={{ padding: 10 }}>
                                            <Table style={{ marginRight: 10 }} bordered striped>
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: 100 }}>No</th>
                                                        <th style={{ width: 150 }}>Box</th>
                                                        <th style={{ width: 450 }}>Probe</th>
                                                        <th style={{ width: 300 }}>Lot</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(() => {
                                                        return getVM()
                                                            .sortedPcrProbes()
                                                            .map((prp: PcrRunProbes, index) => {
                                                                const probe = prp.probe!
                                                                return (
                                                                    <tr key={'refelence-probe-' + probe.id}>
                                                                        <td style={{ width: 20, textAlign: 'center', verticalAlign: 'middle' }}>{index + 1}</td>
                                                                        <td style={{ verticalAlign: 'middle' }}>{probe.box}</td>
                                                                        <td
                                                                            className={hoveredPcrCell.probe?.id === probe.id ? 'flashing' : ''}
                                                                            style={{ backgroundColor: getVM().getProbeColor(probe), verticalAlign: 'middle' }}
                                                                        >
                                                                            <Flex>
                                                                                <div style={{ padding: '2px 5px', borderRadius: 5, backgroundColor: 'white' }}>
                                                                                    {getVM().getProbeNumber(probe)}
                                                                                </div>
                                                                                <div style={{ color: getFontColorByBackground(getVM().getProbeColor(probe)!) }}>
                                                                                    {probe.name}
                                                                                </div>
                                                                            </Flex>
                                                                        </td>
                                                                        <td>
                                                                            <div>{updateStockForm.object[index]?.probeStockId}</div>
                                                                        </td>
                                                                    </tr>
                                                                )
                                                            })
                                                    })()}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </div>

                                    <div style={{ backgroundColor: 'white' }} className="run-required-list">
                                        <div style={{ background: theme.brand, padding: '5px 15px', color: 'white' }}>cDNA</div>

                                        <div style={{ padding: 10 }}>
                                            <Table bordered striped style={{ marginTop: 20 }}>
                                                <thead>
                                                    <tr>
                                                        <th>No</th>
                                                        <th style={{ minWidth: 450 }}>cDNA ID</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(() => {
                                                        const rows = getVM()
                                                            .sortedDnaSet()
                                                            .map((dna, index) => {
                                                                return (
                                                                    <tr key={'refelence-dna-' + dna.id}>
                                                                        <td style={{ width: 20, textAlign: 'center' }}>{index + 1}</td>
                                                                        <td
                                                                            className={
                                                                                typeof hoveredPcrCell.dna !== 'string' && hoveredPcrCell.dna?.id === dna.id
                                                                                    ? 'flashing'
                                                                                    : ''
                                                                            }
                                                                            style={{ backgroundColor: getVM().getDnaColor(dna) }}
                                                                        >
                                                                            <Flex>
                                                                                <div style={{ padding: '2px 5px', borderRadius: 5, backgroundColor: 'white' }}>
                                                                                    {getVM().getDnaNumber(dna)}
                                                                                </div>
                                                                                <div style={{ color: getFontColorByBackground(getVM().getDnaColor(dna)!) }}>
                                                                                    {dna.code}
                                                                                </div>
                                                                            </Flex>
                                                                        </td>
                                                                    </tr>
                                                                )
                                                            })
                                                        return rows
                                                    })()}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </div>
                                </Flex>
                            </div>
                        </Flex>
                    </Landscape>
                )}
            </ContentBody>
        </>
    )
}

export default withRouter(PcrRun384Page)
